import { useMemo } from 'react'
import {
  calculateFreeDropinGrants,
  getRuleForActivity,
  isRuleActive,
  ruleAllowsHelpDancers
} from '../../../pure-js/libs/calculateLogic/FreeDropinTicketLogic'
import {
  FreeDropinRuleType,
  type BulkFreeDropinRule,
  type FreeDropinTicketRule,
  type QualifyingRegistration
} from '../../../pure-js/types/FreeDropinTicketTypes'
import type { Activity } from '../../../pure-js/types/GrooverTypes'
import type { FreeDropinPriorQualificationCount } from '../../../pure-js/types/QueryParamTypes'

export type FreeDropinNextThreshold = {
  additionalQualifyingActivities: number
  additionalTickets: number
  totalTickets: number
}

export type FreeDropinRewardPreviewRow = {
  rule: FreeDropinTicketRule
  periodKey: string
  qualifyingCount: number
  entitledTickets: number
  selectedQualifyingActivityNames: string[]
  nextThreshold?: FreeDropinNextThreshold
}

export type FreeDropinRewardPreview = {
  rows: FreeDropinRewardPreviewRow[]
  totalTickets: number
}

type PreviewInput = {
  rules: FreeDropinTicketRule[]
  selectedActivities: Activity[]
  priorQualificationCounts?: FreeDropinPriorQualificationCount[]
  /** When true, only rules that explicitly include help dancers are previewed. */
  isHelpDancer?: boolean
}

const periodKeyOf = (activity: Activity): string => activity.periodId || activity.period || ''
const rulePeriodKey = (ruleId: string, periodKey: string): string => `${ruleId}::${periodKey}`

const getNextThreshold = (
  rule: BulkFreeDropinRule,
  qualifyingCount: number,
  entitledTickets: number
): FreeDropinNextThreshold | undefined => {
  const nextThreshold = [...rule.thresholds]
    .sort((a, b) => a.qualifyingCount - b.qualifyingCount)
    .find((threshold) => threshold.qualifyingCount > qualifyingCount)

  if (!nextThreshold) return undefined

  return {
    additionalQualifyingActivities: nextThreshold.qualifyingCount - qualifyingCount,
    additionalTickets: nextThreshold.ticketsGranted - entitledTickets,
    totalTickets: nextThreshold.ticketsGranted
  }
}

export const buildFreeDropinRewardPreview = ({
  rules,
  selectedActivities,
  priorQualificationCounts = [],
  isHelpDancer = false
}: PreviewInput): FreeDropinRewardPreview => {
  const activeRules = rules.filter((rule) => isRuleActive(rule) && (!isHelpDancer || ruleAllowsHelpDancers(rule)))
  const selectedRegistrations = selectedActivities.flatMap<QualifyingRegistration>((activity) => {
    const rule = getRuleForActivity(activeRules, activity.id)
    if (!rule) return []
    return [{ activityId: activity.id, periodKey: periodKeyOf(activity) }]
  })
  const selectedRulePeriodKeys = new Set(
    selectedRegistrations.map((registration) => {
      const rule = getRuleForActivity(activeRules, registration.activityId)
      return rulePeriodKey(rule!.id, registration.periodKey)
    })
  )
  const priorQualifyingRegistrations = priorQualificationCounts.flatMap<QualifyingRegistration>((count) => {
    const rule = activeRules.find(
      (candidate) => candidate.id === count.ruleId && (candidate.revision ?? 1) === count.ruleRevision
    )
    if (!rule) return []
    if (rule.ruleType === FreeDropinRuleType.SingleActivity) {
      return rule.activityIds
        .slice(0, count.qualifyingCount)
        .map((activityId) => ({ activityId, periodKey: count.periodKey }))
    }
    const activityId = rule.activityIds[0]
    if (!activityId) return []
    return Array.from({ length: count.qualifyingCount }, () => ({ activityId, periodKey: count.periodKey }))
  })
  const grants = calculateFreeDropinGrants({
    rules: activeRules,
    registrations: [...priorQualifyingRegistrations, ...selectedRegistrations]
  })

  const rows = grants
    .filter((grant) => selectedRulePeriodKeys.has(rulePeriodKey(grant.ruleId, grant.periodKey)))
    .map<FreeDropinRewardPreviewRow>((grant) => {
      const rule = activeRules.find((candidate) => candidate.id === grant.ruleId)!
      const selectedQualifyingActivityNames = selectedActivities
        .filter(
          (activity) =>
            periodKeyOf(activity) === grant.periodKey &&
            getRuleForActivity(activeRules, activity.id)?.id === grant.ruleId
        )
        .map((activity) => activity.name)

      return {
        rule,
        periodKey: grant.periodKey,
        qualifyingCount: grant.qualifyingCount,
        entitledTickets: grant.entitledTickets,
        selectedQualifyingActivityNames,
        nextThreshold:
          rule.ruleType === FreeDropinRuleType.Bulk
            ? getNextThreshold(rule, grant.qualifyingCount, grant.entitledTickets)
            : undefined
      }
    })

  return {
    rows,
    totalTickets: rows.reduce((total, row) => total + row.entitledTickets, 0)
  }
}

export const useFreeDropinRewardPreview = (input: PreviewInput): FreeDropinRewardPreview =>
  useMemo(
    () => buildFreeDropinRewardPreview(input),
    [input.rules, input.selectedActivities, input.priorQualificationCounts, input.isHelpDancer]
  )
